import { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { createClient } from '@supabase/supabase-js'

// Check if environment variables are set
const supabaseUrl = process.env.REACT_APP_SUPABASE_URL
const supabaseKey = process.env.REACT_APP_SUPABASE_ANON_KEY

let supabase = null
if (supabaseUrl && supabaseKey && supabaseUrl !== 'https://your-project.supabase.co') {
  supabase = createClient(supabaseUrl, supabaseKey)
}

export default function AuthCallback() {
  const navigate = useNavigate()

  useEffect(() => {
    const handleAuth = async () => {
      if (!supabase) {
        console.log('Supabase not configured - redirecting to home')
        navigate('/')
        return
      }

      const params = new URLSearchParams(window.location.search)
      const code = params.get('code')
      const token = params.get('token') // For PKCE flow (legacy)
      const tokenHash = params.get('token_hash') // For OTP flow (correct)
      const type = params.get('type') // Supabase sends type parameter for different auth flows
      const error = params.get('error')
      const errorCode = params.get('error_code')
      const errorDescription = params.get('error_description')

      // Check for auth errors (like expired OTP links)
      if (error || errorCode) {
        let errorMessage = 'Authentication failed'
        if (errorCode === 'otp_expired') {
          errorMessage = 'Email link has expired. Please request a new confirmation email.'
        } else if (errorDescription) {
          errorMessage = decodeURIComponent(errorDescription.replace(/\+/g, ' '))
        }
        navigate('/email-confirmed?status=error&message=' + encodeURIComponent(errorMessage))
        return
      }

      // Handle both regular OAuth code flow and OTP token flow
      if (code || token || tokenHash) {
        try {
          let result
          if (code) {
            // Regular OAuth flow
            result = await supabase.auth.exchangeCodeForSession(code)
          } else if (tokenHash) {
            // OTP flow (correct) - verify OTP with token_hash
            result = await supabase.auth.verifyOtp({
              token_hash: tokenHash,
              type: type || 'signup'
            })
          } else if (token) {
            // Legacy PKCE flow - verify OTP with token
            result = await supabase.auth.verifyOtp({
              token_hash: token,
              type: type || 'signup'
            })
          }

          if (result.error) {
            console.error('Auth error:', result.error.message)
            // Redirect to confirmation page with error
            navigate('/email-confirmed?status=error&message=' + encodeURIComponent(result.error.message))
          } else {
            console.log('Auth successful:', result.data.user)
            
            // Check if this is an email confirmation (signup confirmation)
            if (type === 'signup' || type === 'email_change' || type === 'invite') {
              // Email confirmation successful
              navigate('/email-confirmed?status=success')
            } else {
              // Regular login - redirect to home page
              navigate('/?authenticated=true')
            }
          }
        } catch (error) {
          console.error('Unexpected error:', error)
          navigate('/email-confirmed?status=error&message=' + encodeURIComponent('An unexpected error occurred'))
        }
      } else {
        // No code, token, or token_hash parameter, redirect to home
        navigate('/')
      }
    }

    handleAuth()
  }, [navigate])

  return (
    <div style={{ padding: '50px', textAlign: 'center' }}>
      <h2>Verifying your email...</h2>
      <p>Please wait while we confirm your email address.</p>
      {!supabase && (
        <div style={{ marginTop: '20px', padding: '20px', backgroundColor: '#f0f0f0', borderRadius: '8px' }}>
          <p><strong>Note:</strong> Supabase authentication is not configured yet.</p>
          <p>Please update the environment variables in .env file with your Supabase project details.</p>
        </div>
      )}
    </div>
  )
}

